import React, { useState } from 'react';
import { IoClose, IoChevronBack, IoFolder, IoDocument, IoInformationCircle } from "react-icons/io5";
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import api from '../../services/api';

const isReadmeOrMarkdown = (name) => {
    const n = (name || '').toLowerCase();
    return n.endsWith('.md') || n === 'readme';
};

const Artifacts = ({ isOpen, onClose }) => {
    const [view, setView] = useState('menu'); // 'menu' | 'backend_folders' | 'backend_files' | 'frontend_folders' | 'frontend_files' | 'file_view'
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [fileContent, setFileContent] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [filePath, setFilePath] = useState(null); // full path for API

    const [fileViewSource, setFileViewSource] = useState(null); // 'backend' | 'frontend'
    const [showEditPrompt, setShowEditPrompt] = useState(false);
    const [editPrompt, setEditPrompt] = useState('');
    const [newContent, setNewContent] = useState(null); // LLM-modified content for diff view
    const [commitMessage, setCommitMessage] = useState('');

    // Enhanced: Form context support
    const [formName, setFormName] = useState('');
    const [useKnowledgeGraph, setUseKnowledgeGraph] = useState(false);
    const [contextInfo, setContextInfo] = useState(null);

    const handleClose = () => {
        setView('menu');
        setBreadcrumb([]);
        setFolders([]);
        setFiles([]);
        setError(null);
        setFileContent(null);
        setFileName(null);
        setFilePath(null);
        setFileViewSource(null);
        setShowEditPrompt(false);
        setEditPrompt('');
        setNewContent(null);
        setSuccessMessage(null);
        setCommitMessage('');
        setFormName('');
        setUseKnowledgeGraph(false);
        setContextInfo(null);
        onClose();
    };

    const loadBackendContents = async (path = '') => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const data = await api.artifacts.getBackendContents(path);
            setFolders(data.folders || []);
            setFiles(data.files || []);
        } catch (err) {
            setError(err.message || 'Failed to load contents');
            setFolders([]);
            setFiles([]);
        } finally {
            setLoading(false);
        }
    };

    const loadFrontendContents = async (path = '') => {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const data = await api.artifacts.getFrontendContents(path);
            setFolders(data.folders || []);
            setFiles(data.files || []);
        } catch (err) {
            setError(err.message || 'Failed to load contents');
            setFolders([]);
            setFiles([]);
        } finally {
            setLoading(false);
        }
    };

    const handleBackendClick = () => {
        setView('backend_folders');
        setBreadcrumb([]);
        loadBackendContents('');
    };

    const handleFrontendClick = () => {
        setView('frontend_folders');
        setBreadcrumb([]);
        loadFrontendContents('');
    };

    const handleFolderClick = (folderName) => {
        const newBreadcrumb = [...breadcrumb, folderName];
        const path = newBreadcrumb.join('/');
        setBreadcrumb(newBreadcrumb);
        if (view === 'backend_folders' || view === 'backend_files') {
            setView('backend_files');
            loadBackendContents(path);
        } else if (view === 'frontend_folders' || view === 'frontend_files') {
            setView('frontend_files');
            loadFrontendContents(path);
        }
    };

    const handleBack = () => {
        setSuccessMessage(null);
        if (view === 'file_view') {
            if (newContent) {
                setNewContent(null);
                setCommitMessage('');
            } else if (showEditPrompt) {
                setShowEditPrompt(false);
                setEditPrompt('');
            } else {
                setView(fileViewSource === 'backend' ? 'backend_files' : 'frontend_files');
                setFileContent(null);
                setFileName(null);
                setFilePath(null);
            }
            return;
        }
        if (breadcrumb.length > 1) {
            const newBreadcrumb = breadcrumb.slice(0, -1);
            const path = newBreadcrumb.join('/');
            setBreadcrumb(newBreadcrumb);
            if (view === 'backend_files') loadBackendContents(path);
            else if (view === 'frontend_files') loadFrontendContents(path);
        } else if (breadcrumb.length === 1) {
            setBreadcrumb([]);
            if (view === 'backend_files') {
                setView('backend_folders');
                loadBackendContents('');
            } else if (view === 'frontend_files') {
                setView('frontend_folders');
                loadFrontendContents('');
            }
        } else if (view === 'backend_folders' || view === 'frontend_folders') {
            setView('menu');
            setFolders([]);
            setFiles([]);
        }
    };

    const handleFileClick = async (file) => {
        const path = breadcrumb.length > 0 ? `${breadcrumb.join('/')}/${file}` : file;
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const isBackend = view === 'backend_files';
            setFileViewSource(isBackend ? 'backend' : 'frontend');
            const data = isBackend
                ? await api.artifacts.getBackendFile(path)
                : await api.artifacts.getFrontendFile(path);
            setFileContent(data.content);
            setFileName(data.filename);
            setFilePath(path);
            setView('file_view');
            setShowEditPrompt(false);
            setEditPrompt('');
            setNewContent(null);
        } catch (err) {
            setError(err.message || 'Failed to load file');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitEdit = async () => {
        if (!editPrompt.trim()) return;
        const baseContent = newContent ?? fileContent ?? '';
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        setContextInfo(null);
        try {
            let data;
            if (useKnowledgeGraph && formName.trim()) {
                // Use enhanced edit with Knowledge Graph context
                data = await api.artifacts.editFileEnhanced(
                    baseContent, 
                    editPrompt.trim(), 
                    fileName, 
                    fileViewSource,
                    formName.trim(),
                    true
                );
                if (data.context_used) {
                    setContextInfo(data.context_used);
                }
            } else {
                // Use standard edit
                data = await api.artifacts.editFile(baseContent, editPrompt.trim(), fileName, fileViewSource);
            }
            setNewContent(data.content);
            setShowEditPrompt(false);
            setEditPrompt('');
        } catch (err) {
            const msg = err.name === 'AbortError' ? 'Edit timed out (LLM took too long). Try a shorter prompt or smaller file.' : (err.message || 'Edit failed');
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleEditAgain = () => {
        setSuccessMessage(null);
        setShowEditPrompt(true);
        setEditPrompt('');
    };

    const handleCancelEdit = () => {
        setSuccessMessage(null);
        setNewContent(null);
        setCommitMessage('');
    };

    const handleCommit = async () => {
        const contentToCommit = newContent;
        if (!contentToCommit) return;
        setLoading(true);
        setError(null);
        setSuccessMessage(null);
        try {
            await api.artifacts.commitFile(filePath, contentToCommit, fileViewSource, commitMessage || `Edit ${fileName} via Artifacts UI`);
            setFileContent(contentToCommit);
            setNewContent(null);
            setCommitMessage('');
            setSuccessMessage(`Successfully committed changes to ${fileName}`);
        } catch (err) {
            setError(err.message || 'Commit failed');
        } finally {
            setLoading(false);
        }
    };

    // VS Code-style diff: side-by-side, word-level changes, line numbers
    const diffViewerStyles = {
        variables: {
            dark: {
                diffViewerBackground: '#1e1e1e',
                diffViewerColor: '#d4d4d4',
                addedBackground: 'rgba(46, 160, 67, 0.25)',
                addedColor: '#4ec9b0',
                removedBackground: 'rgba(248, 81, 73, 0.25)',
                removedColor: '#f14c4c',
                wordAddedBackground: 'rgba(46, 160, 67, 0.5)',
                wordRemovedBackground: 'rgba(248, 81, 73, 0.5)',
                gutterBackground: '#252526',
                gutterColor: '#858585',
                addedGutterBackground: 'rgba(46, 160, 67, 0.35)',
                removedGutterBackground: 'rgba(248, 81, 73, 0.35)',
            },
        },
        line: {
            padding: '2px 0',
            fontFamily: '"Consolas", "Monaco", "Courier New", monospace',
            fontSize: '13px',
        },
    };

    const renderDiffView = () => (
        <div className="flex-1 min-h-0 overflow-auto" style={{ backgroundColor: '#1e1e1e' }}>
            <ReactDiffViewer
                oldValue={fileContent ?? ''}
                newValue={newContent ?? ''}
                splitView={true}
                compareMethod={DiffMethod.WORDS}
                showDiffOnly={false}
                useDarkTheme={true}
                leftTitle="Original"
                rightTitle="Modified"
                styles={diffViewerStyles}
                extraLinesSurroundingDiff={2}
            />
        </div>
    );

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleClose}
        >
            <div 
                className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh]"
                style={{ backgroundColor: 'var(--bg-primary)' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-3">
                        {(view === 'backend_folders' || view === 'backend_files' || view === 'frontend_folders' || view === 'frontend_files' || view === 'file_view') && (
                            <button onClick={handleBack} className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-1" style={{ color: 'var(--text-primary)' }}>
                                <IoChevronBack className="w-5 h-5" /> Back
                            </button>
                        )}
                        <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                            Artifacts
                            {view === 'backend_folders' && ' — Backend'}
                            {view === 'backend_files' && breadcrumb.length > 0 && ` — ${breadcrumb.join(' / ')}`}
                            {view === 'frontend_folders' && ' — Frontend'}
                            {view === 'frontend_files' && breadcrumb.length > 0 && ` — ${breadcrumb.join(' / ')}`}
                            {view === 'file_view' && fileName && ` — ${fileName}`}
                        </h3>
                    </div>
                    <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: 'var(--text-primary)' }}>
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                <div className="flex-1 overflow-hidden p-6">
                    <div className="overflow-y-auto h-full">
                        {view === 'menu' && (
                            <div className="grid gap-4">
                                <div
                                    onClick={handleBackendClick}
                                    className="flex items-center justify-between p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer"
                                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
                                >
                                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>Backend</div>
                                </div>
                                <div
                                    onClick={handleFrontendClick}
                                    className="flex items-center justify-between p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer"
                                    style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
                                >
                                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>Frontend</div>
                                </div>
                            </div>
                        )}

                        {(view === 'backend_folders' || view === 'backend_files' || view === 'frontend_folders' || view === 'frontend_files' || view === 'file_view') && (
                            <>
                                {error && <div className="mb-4 p-4 rounded-lg text-red-600" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)' }}>{error}</div>}
                                {successMessage && <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)', color: 'rgb(22, 163, 74)' }}>{successMessage}</div>}
                                {loading ? (
                                    <div className="flex items-center justify-center py-12" style={{ color: 'var(--text-secondary)' }}>Loading...</div>
                                ) : view === 'file_view' ? (
                                    <div className="h-full flex flex-col min-h-0">
                                        {newContent ? (
                                            <>
                                                <div className="mb-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                                    Review the diff, use Refine to apply another prompt, then Commit.
                                                </div>
                                                <div
                                                    className="flex-1 min-h-0 rounded-lg overflow-hidden flex flex-col"
                                                    style={{
                                                        backgroundColor: 'var(--bg-secondary)',
                                                        border: '1px solid',
                                                        borderColor: 'var(--border-color)',
                                                    }}
                                                >
                                                    {renderDiffView()}
                                                </div>
                                                <div className="flex-shrink-0 pt-4 mt-4 border-t flex flex-col gap-2" style={{ borderColor: 'var(--border-color)' }}>
                                                    {showEditPrompt ? (
                                                        <>
                                                            <textarea
                                                                value={editPrompt}
                                                                onChange={(e) => setEditPrompt(e.target.value)}
                                                                placeholder="Enter your prompt (e.g. fix the bug, add validation...)"
                                                                className="w-full p-3 rounded-lg border text-sm resize-none"
                                                                rows={3}
                                                                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                                                            />
                                                            <div className="flex gap-2 justify-end">
                                                                <button onClick={() => { setSuccessMessage(null); setShowEditPrompt(false); setEditPrompt(''); }} className="px-4 py-2 rounded-lg font-medium border"
                                                                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                                                                    Cancel
                                                                </button>
                                                                <button onClick={handleSubmitEdit} disabled={loading} className="px-4 py-2 rounded-lg font-medium"
                                                                    style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }}>
                                                                    {loading ? 'Submitting...' : 'Submit'}
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <input
                                                                type="text"
                                                                value={commitMessage}
                                                                onChange={(e) => setCommitMessage(e.target.value)}
                                                                placeholder="Commit message (optional)"
                                                                className="w-full p-2 rounded-lg border text-sm"
                                                                style={{
                                                                    backgroundColor: 'var(--bg-secondary)',
                                                                    borderColor: 'var(--border-color)',
                                                                    color: 'var(--text-primary)',
                                                                }}
                                                            />
                                                            <div className="flex gap-2 justify-end">
                                                                <button onClick={handleEditAgain} className="px-4 py-2 rounded-lg font-medium border"
                                                                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                                                                    Refine
                                                                </button>
                                                                <button onClick={handleCancelEdit} className="px-4 py-2 rounded-lg font-medium border"
                                                                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                                                                    Cancel
                                                                </button>
                                                                <button onClick={handleCommit} className="px-4 py-2 rounded-lg font-medium"
                                                                    style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }}>
                                                                    Commit
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div
                                                    className="flex-1 min-h-0 rounded-lg overflow-auto"
                                                    style={{
                                                        backgroundColor: 'var(--bg-secondary)',
                                                        border: '1px solid',
                                                        borderColor: 'var(--border-color)',
                                                    }}
                                                >
                                                    <div className="p-4 font-mono text-sm">
                                                        {((fileContent ?? '') || '(empty file)').split('\n').map((line, i) => (
                                                            <div key={i} className="flex leading-relaxed">
                                                                <div className="flex-shrink-0 w-12 pr-3 text-right select-none" style={{ color: 'var(--text-secondary)' }}>{i + 1}</div>
                                                                <pre className="flex-1 whitespace-pre-wrap break-words min-w-0 m-0" style={{ color: 'var(--text-primary)' }}>{line || '\u00A0'}</pre>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex-shrink-0 pt-4 mt-4 border-t flex flex-col gap-2" style={{ borderColor: 'var(--border-color)' }}>
                                                    {!isReadmeOrMarkdown(fileName) && (
                                                    !showEditPrompt ? (
                                                        <button onClick={() => { setSuccessMessage(null); setShowEditPrompt(true); }} className="self-end px-4 py-2 rounded-lg font-medium transition-all"
                                                            style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }}>
                                                            Edit
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {/* Knowledge Graph Context Toggle */}
                                                            <div className="p-3 rounded-lg mb-2" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        id="useKG"
                                                                        checked={useKnowledgeGraph}
                                                                        onChange={(e) => setUseKnowledgeGraph(e.target.checked)}
                                                                        className="w-4 h-4"
                                                                    />
                                                                    <label htmlFor="useKG" className="text-sm font-medium cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                                                                        Use Knowledge Graph Context
                                                                    </label>
                                                                    <IoInformationCircle className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} title="Include Oracle Form structure, business rules, and coding standards" />
                                                                </div>
                                                                {useKnowledgeGraph && (
                                                                    <input
                                                                        type="text"
                                                                        value={formName}
                                                                        onChange={(e) => setFormName(e.target.value.toUpperCase())}
                                                                        placeholder="Form name (e.g., EPRO023)"
                                                                        className="w-full p-2 rounded border text-sm"
                                                                        style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                                                                    />
                                                                )}
                                                            </div>
                                                            <textarea
                                                                value={editPrompt}
                                                                onChange={(e) => setEditPrompt(e.target.value)}
                                                                placeholder="Enter your prompt (e.g. fix the bug, add validation...)"
                                                                className="w-full p-3 rounded-lg border text-sm resize-none"
                                                                rows={3}
                                                                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                                                            />
                                                            {contextInfo && (
                                                                <div className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#16a34a' }}>
                                                                    Context used: {contextInfo.knowledge_graph ? 'Knowledge Graph' : ''} {contextInfo.rag ? 'RAG' : ''} {contextInfo.form_name ? `(${contextInfo.form_name})` : ''}
                                                                </div>
                                                            )}
                                                            <div className="flex gap-2 justify-end">
                                                                <button onClick={() => { setSuccessMessage(null); setShowEditPrompt(false); setEditPrompt(''); setContextInfo(null); }} className="px-4 py-2 rounded-lg font-medium border"
                                                                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
                                                                    Cancel
                                                                </button>
                                                                <button onClick={handleSubmitEdit} disabled={loading} className="px-4 py-2 rounded-lg font-medium"
                                                                    style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }}>
                                                                    {loading ? 'Submitting...' : 'Submit'}
                                                                </button>
                                                            </div>
                                                        </>
                                                    )
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {(view === 'backend_folders' || view === 'frontend_folders' || view === 'backend_files' || view === 'frontend_files') && folders.map((folder) => (
                                            <div key={folder} onClick={() => handleFolderClick(folder)}
                                                className="flex items-center gap-4 p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer"
                                                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                                                <IoFolder className="w-5 h-5" style={{ color: 'var(--blue-primary)' }} />
                                                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{folder}</div>
                                            </div>
                                        ))}
                                        {(view === 'backend_folders' || view === 'frontend_folders') && folders.length === 0 && !loading && !error && (
                                            <div className="py-8 text-center" style={{ color: 'var(--text-secondary)' }}>
                                                No folders found in {view === 'backend_folders' ? 'generated/backend/nodejs' : 'generated/frontend/angular'}
                                            </div>
                                        )}
                                        {(view === 'backend_files' || view === 'frontend_files') && files.map((file) => (
                                            <div key={file} onClick={() => handleFileClick(file)}
                                                className="flex items-center gap-4 p-4 rounded-lg border hover:shadow-md transition-all cursor-pointer"
                                                style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                                                <IoDocument className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
                                                <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{file}</div>
                                            </div>
                                        ))}
                                        {(view === 'backend_files' || view === 'frontend_files') && folders.length === 0 && files.length === 0 && !loading && !error && (
                                            <div className="py-8 text-center" style={{ color: 'var(--text-secondary)' }}>No folders or files in this path</div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Artifacts;
